/**
 * TutorPcmPlayer — PCM16 LE mono playback для озвучки ответов репетитора.
 *
 * PCM → WAV Blob → HTMLAudioElement с `preservesPitch` (как YouTube /
 * audio-recap): ускорение без сдвига тона. Analyser для волны —
 * MediaElementSource → Gain → Analyser.
 *
 * Progressive: early-start после ~0.4 с PCM; на finish() — swap на полный
 * WAV с seek на текущую позицию.
 *
 * Используется в useTutorMessageTts → POST /api/tutor/tts/stream.
 */

const DEFAULT_SAMPLE_RATE = 24_000;
/** Байты PCM16 mono для early-start (~0.4 с). */
const EARLY_START_SEC = 0.4;

function getAudioContextCtor(): typeof AudioContext | undefined {
	if (typeof window === "undefined") return undefined;
	return (
		window.AudioContext ??
		(window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
	);
}

export interface TutorPcmPlayerOptions {
	/** Sample rate в Hz (default 24000). */
	sampleRate?: number;
	/** Громкость 0..1 (default 1.0). */
	volume?: number;
	/** Playback rate (default 1.0). */
	playbackRate?: number;
	/** Callback когда трек доигран. */
	onEnd?: () => void;
	/** Callback при создании AnalyserNode (для визуализации волны). */
	onAnalyser?: (node: AnalyserNode | null) => void;
	/** Callback при реальном старте звука (loading → playing). */
	onPlaying?: () => void;
}

function concatUint8(
	a: Uint8Array<ArrayBuffer>,
	b: Uint8Array<ArrayBufferLike>,
): Uint8Array<ArrayBuffer> {
	const out = new Uint8Array(a.byteLength + b.byteLength);
	out.set(a, 0);
	out.set(b, a.byteLength);
	return out;
}

/** PCM16 LE mono → WAV Blob. */
export function pcm16LeToWavBlob(pcm: Uint8Array, sampleRate: number): Blob {
	const dataSize = pcm.byteLength - (pcm.byteLength % 2);
	const buffer = new ArrayBuffer(44 + dataSize);
	const view = new DataView(buffer);
	const channels = 1;
	const bitsPerSample = 16;
	const blockAlign = (channels * bitsPerSample) / 8;
	const byteRate = sampleRate * blockAlign;

	const writeStr = (offset: number, s: string) => {
		for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
	};

	writeStr(0, "RIFF");
	view.setUint32(4, 36 + dataSize, true);
	writeStr(8, "WAVE");
	writeStr(12, "fmt ");
	view.setUint32(16, 16, true);
	view.setUint16(20, 1, true);
	view.setUint16(22, channels, true);
	view.setUint32(24, sampleRate, true);
	view.setUint32(28, byteRate, true);
	view.setUint16(32, blockAlign, true);
	view.setUint16(34, bitsPerSample, true);
	writeStr(36, "data");
	view.setUint32(40, dataSize, true);
	new Uint8Array(buffer, 44).set(pcm.subarray(0, dataSize));
	return new Blob([buffer], { type: "audio/wav" });
}

function applyPreservesPitch(audio: HTMLAudioElement): void {
	audio.preservesPitch = true;
	const legacy = audio as HTMLAudioElement & {
		mozPreservesPitch?: boolean;
		webkitPreservesPitch?: boolean;
	};
	legacy.mozPreservesPitch = true;
	legacy.webkitPreservesPitch = true;
}

const activePlayers = new Set<TutorPcmPlayer>();

/** Shared warm context — создаётся в user-gesture и живёт между Speak. */
let sharedAudioContext: AudioContext | null = null;

export class TutorPcmPlayer {
	static stopAllExcept(self: TutorPcmPlayer | null): void {
		for (const p of Array.from(activePlayers)) {
			if (p !== self) p.stop();
		}
	}

	/**
	 * Создать/resume AudioContext синхронно в user-gesture (iOS/WKWebView).
	 * Нужен для MediaElementSource → Analyser (волна в pill).
	 */
	static unlockFromUserGesture(): void {
		const Ctor = getAudioContextCtor();
		if (!Ctor) return;
		try {
			if (!sharedAudioContext || sharedAudioContext.state === "closed") {
				sharedAudioContext = new Ctor({ sampleRate: DEFAULT_SAMPLE_RATE });
			}
			if (sharedAudioContext.state === "suspended") {
				void sharedAudioContext.resume();
			}
		} catch {
			/* ignore */
		}
	}

	/** @internal Vitest: сброс shared ctx, чтобы stub AudioContext подхватывался. */
	static resetSharedContextForTests(): void {
		if (sharedAudioContext && sharedAudioContext.state !== "closed") {
			void sharedAudioContext.close().catch(() => {
				/* ignore */
			});
		}
		sharedAudioContext = null;
	}

	private readonly sampleRate: number;
	private readonly earlyStartBytes: number;
	private pcm: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(0));
	/** Сколько PCM-байт уже отдано в текущий object URL. */
	private publishedPcmBytes = 0;
	private ctx: AudioContext | null = null;
	private gainNode: GainNode | null = null;
	private analyserNode: AnalyserNode | null = null;
	private mediaSourceNode: MediaElementAudioSourceNode | null = null;
	private audio: HTMLAudioElement | null = null;
	private objectUrl: string | null = null;
	private stopped = false;
	private finished = false;
	private startedPlaying = false;
	private playStarted = false;
	private amplitudeWatchRaf: number | undefined;
	private amplitudeWatchStartedAt = 0;
	private volume: number;
	private playbackRate: number;
	private onEnd: (() => void) | undefined;
	private onAnalyser: ((node: AnalyserNode | null) => void) | undefined;
	private onPlaying: (() => void) | undefined;
	private readonly onAudioEnded = (): void => {
		if (this.stopped) return;
		// Partial early-start закончился до finish — ждём upgrade / finish.
		if (!this.finished) {
			const aligned = this.alignedPcm();
			if (aligned.byteLength > this.publishedPcmBytes) {
				this.upgradeHtmlAudio(aligned);
			}
			return;
		}
		this.notifyEnd();
	};
	private readonly onAudioError = (): void => {
		if (this.stopped) return;
		this.notifyEnd();
	};

	constructor(options: TutorPcmPlayerOptions = {}) {
		this.sampleRate = options.sampleRate ?? DEFAULT_SAMPLE_RATE;
		this.earlyStartBytes = Math.floor(this.sampleRate * 2 * EARLY_START_SEC);
		this.volume = options.volume ?? 1.0;
		this.playbackRate = options.playbackRate ?? 1.0;
		this.onEnd = options.onEnd;
		this.onAnalyser = options.onAnalyser;
		this.onPlaying = options.onPlaying;
		activePlayers.add(this);
	}

	private alignedPcm(): Uint8Array {
		const aligned = this.pcm.byteLength - (this.pcm.byteLength % 2);
		return this.pcm.subarray(0, aligned);
	}

	private ensureContext(): AudioContext {
		if (this.ctx == null) {
			if (sharedAudioContext && sharedAudioContext.state !== "closed") {
				this.ctx = sharedAudioContext;
			} else {
				const Ctor = getAudioContextCtor();
				if (!Ctor) {
					throw new Error("AudioContext is not available");
				}
				this.ctx = new Ctor({ sampleRate: this.sampleRate });
				sharedAudioContext = this.ctx;
			}
		}
		if (this.ctx.state === "suspended") {
			void this.ctx.resume();
		}
		return this.ctx;
	}

	/** Записать PCM-чанк; early-start после ~0.4 с. */
	append(bytes: Uint8Array): void {
		if (this.stopped || this.finished) return;
		if (bytes.byteLength === 0) return;
		this.pcm = concatUint8(this.pcm, bytes);
		const aligned = this.alignedPcm();
		if (!this.playStarted && aligned.byteLength >= this.earlyStartBytes) {
			this.startHtmlAudio(aligned);
		}
	}

	/**
	 * Ввод завершён — старт или upgrade до полного WAV.
	 * Пустой PCM → сразу onEnd.
	 */
	finish(): void {
		if (this.finished || this.stopped) return;
		this.finished = true;
		const aligned = this.alignedPcm();
		if (aligned.byteLength < 2) {
			this.notifyEnd();
			return;
		}
		if (!this.playStarted) {
			this.startHtmlAudio(aligned);
		} else if (aligned.byteLength > this.publishedPcmBytes) {
			this.upgradeHtmlAudio(aligned);
		}
	}

	private ensureGraph(audio: HTMLAudioElement): void {
		const ctx = this.ensureContext();
		if (this.gainNode == null) {
			this.gainNode = ctx.createGain();
			this.gainNode.gain.value = this.volume;
		}
		if (this.analyserNode == null) {
			this.analyserNode = ctx.createAnalyser();
			this.analyserNode.fftSize = 256;
			this.analyserNode.smoothingTimeConstant = 0.7;
			this.gainNode.connect(this.analyserNode);
			this.analyserNode.connect(ctx.destination);
			this.onAnalyser?.(this.analyserNode);
		}
		if (this.mediaSourceNode == null) {
			this.mediaSourceNode = ctx.createMediaElementSource(audio);
			this.mediaSourceNode.connect(this.gainNode);
		}
	}

	private startHtmlAudio(pcm: Uint8Array): void {
		if (this.stopped || this.playStarted) return;
		this.playStarted = true;
		this.publishedPcmBytes = pcm.byteLength;

		const blob = pcm16LeToWavBlob(pcm, this.sampleRate);
		const url = URL.createObjectURL(blob);
		this.objectUrl = url;

		const audio = new Audio();
		this.audio = audio;
		applyPreservesPitch(audio);
		audio.playbackRate = this.playbackRate;
		audio.volume = this.volume;
		audio.preload = "auto";
		audio.src = url;

		this.ensureGraph(audio);
		audio.addEventListener("ended", this.onAudioEnded);
		audio.addEventListener("error", this.onAudioError);

		this.waitForAudibleAmplitude();
		void audio.play().catch(() => {
			/* autoplay / gesture */
		});
	}

	/** Заменить src на более полный WAV, сохранив позицию. */
	private upgradeHtmlAudio(pcm: Uint8Array): void {
		const audio = this.audio;
		if (!audio || this.stopped) {
			if (!this.playStarted) this.startHtmlAudio(pcm);
			return;
		}
		const seekTo = audio.currentTime;
		if (this.objectUrl) {
			URL.revokeObjectURL(this.objectUrl);
			this.objectUrl = null;
		}
		this.publishedPcmBytes = pcm.byteLength;
		const blob = pcm16LeToWavBlob(pcm, this.sampleRate);
		const url = URL.createObjectURL(blob);
		this.objectUrl = url;

		const onMeta = () => {
			audio.removeEventListener("loadedmetadata", onMeta);
			if (this.stopped || this.audio !== audio) return;
			const dur = Number.isFinite(audio.duration) ? audio.duration : seekTo;
			audio.currentTime = Math.min(seekTo, Math.max(0, dur));
			applyPreservesPitch(audio);
			audio.playbackRate = this.playbackRate;
			void audio.play().catch(() => {
				/* ignore */
			});
		};
		audio.addEventListener("loadedmetadata", onMeta);
		audio.src = url;
		audio.load();
	}

	/**
	 * Ждёт реальную амплитуду (> 2%) перед onPlaying — TTS часто начинается
	 * с тихого lead-in. Safety-кап 2.5 с.
	 */
	private waitForAudibleAmplitude(): void {
		const analyser = this.analyserNode;
		if (analyser == null) {
			if (!this.startedPlaying) {
				this.startedPlaying = true;
				this.onPlaying?.();
			}
			return;
		}

		const AMP_THRESHOLD = 0.02;
		const MAX_WAIT_MS = 2500;
		const bufferLength = analyser.frequencyBinCount;
		const data = new Uint8Array(bufferLength);
		this.amplitudeWatchStartedAt = performance.now();

		const check = () => {
			if (this.stopped || this.startedPlaying) return;

			analyser.getByteTimeDomainData(data);
			let min = 255;
			let max = 0;
			for (let i = 0; i < bufferLength; i++) {
				const v = data[i] ?? 128;
				if (v < min) min = v;
				if (v > max) max = v;
			}
			const amp = (max - min) / 255;

			const elapsed = performance.now() - this.amplitudeWatchStartedAt;
			if (amp > AMP_THRESHOLD || elapsed > MAX_WAIT_MS) {
				this.startedPlaying = true;
				this.amplitudeWatchRaf = undefined;
				this.onPlaying?.();
				return;
			}

			this.amplitudeWatchRaf = requestAnimationFrame(check);
		};

		this.amplitudeWatchRaf = requestAnimationFrame(check);
	}

	private notifyEnd(): void {
		activePlayers.delete(this);
		const cb = this.onEnd;
		this.onEnd = undefined;
		cb?.();
	}

	/** Изменить громкость (0..1). */
	setVolume(value: number): void {
		this.volume = Math.max(0, Math.min(1, value));
		if (this.audio) this.audio.volume = this.volume;
		if (this.gainNode) this.gainNode.gain.value = this.volume;
	}

	/**
	 * Изменить playback rate (0.25..4.0).
	 * HTMLAudio + preservesPitch — темп меняется, тон сохраняется.
	 */
	setPlaybackRate(value: number): void {
		const rate = Math.max(0.25, Math.min(4, value));
		this.playbackRate = rate;
		if (this.audio) {
			applyPreservesPitch(this.audio);
			this.audio.playbackRate = rate;
		}
	}

	pause(): void {
		this.audio?.pause();
		void this.ctx?.suspend();
	}

	resume(): void {
		void this.ctx?.resume();
		void this.audio?.play().catch(() => {
			/* ignore */
		});
	}

	/** Полная остановка + cleanup. */
	stop(): void {
		activePlayers.delete(this);
		this.stopped = true;
		if (this.amplitudeWatchRaf != null) {
			cancelAnimationFrame(this.amplitudeWatchRaf);
			this.amplitudeWatchRaf = undefined;
		}

		if (this.audio) {
			this.audio.removeEventListener("ended", this.onAudioEnded);
			this.audio.removeEventListener("error", this.onAudioError);
			this.audio.pause();
			this.audio.removeAttribute("src");
			this.audio.load();
			this.audio = null;
		}
		if (this.objectUrl) {
			URL.revokeObjectURL(this.objectUrl);
			this.objectUrl = null;
		}
		if (this.mediaSourceNode) {
			try {
				this.mediaSourceNode.disconnect();
			} catch {
				/* already disconnected */
			}
			this.mediaSourceNode = null;
		}
		if (this.gainNode) {
			try {
				this.gainNode.disconnect();
			} catch {
				/* already disconnected */
			}
			this.gainNode = null;
		}
		if (this.analyserNode) {
			try {
				this.analyserNode.disconnect();
			} catch {
				/* already disconnected */
			}
			this.analyserNode = null;
		}
		this.pcm = new Uint8Array(new ArrayBuffer(0));
		this.publishedPcmBytes = 0;
		// Shared ctx НЕ закрываем — gesture-warmed context нужен для следующего Speak.
		if (this.ctx && this.ctx !== sharedAudioContext && this.ctx.state !== "closed") {
			void this.ctx.close().catch(() => {
				/* already closed */
			});
		}
		this.ctx = null;
	}

	/** AnalyserNode для визуализации (null до startHtmlAudio). */
	get analyser(): AnalyserNode | null {
		return this.analyserNode;
	}

	/** Длительность накопленного PCM (секунды), без учёта playbackRate. */
	get duration(): number {
		const samples = Math.floor(this.pcm.byteLength / 2);
		return samples / this.sampleRate;
	}
}
